import { createContext, useContext, useMemo, useState } from "react";

import ENDPOINTS from "../../api/endpoint";
import Service from "../../api/service";

const AdminContext = createContext(null);

export const useAdminContext = () => {
  const v = useContext(AdminContext);
  if (!v) {
    throw new Error("useAdminContext must be used inside AdminProvider");
  }
  return v;
};

// Hydrate the admin session synchronously from localStorage on mount so
// RequireAuth doesn't flash a redirect before the effect runs.
const readPersistedAdmin = () => {
  try {
    const raw = localStorage.getItem("auth");
    if (!raw || raw === "undefined" || raw === "null") return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// Standardized error-to-response shape for the few calls that wrap their own
// try/catch. Keeps the return contract consistent with the rest of Service.
const errorResponse = (error, fallbackMessage) => ({
  status: error?.response?.status ?? 500,
  message: error?.response?.data?.message ?? fallbackMessage,
});

export const AdminProvider = ({ children }) => {
  const [sysAdmin, setSysAdmin] = useState(readPersistedAdmin);
  const [singleAdmin, setSingleAdmin] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(0);
  const [companyName, setCompanyName] = useState(null);

  const adminLogin = async (requestData) => {
    try {
      const { data, message, status } = await Service.post(
        ENDPOINTS.LOGIN_URL,
        requestData,
      );
      setSysAdmin(data ?? null);
      if (data) {
        localStorage.setItem("auth", JSON.stringify(data));
        setCompanyName(data?.company_name ?? null);
      } else {
        setCompanyName(null);
      }
      return { message, status };
    } catch (error) {
      return errorResponse(error, "Login failed. Please try again.");
    }
  };

  const adminSignUp = async (requestData) =>
    Service.post(ENDPOINTS.SIGNUP_URL, requestData);

  const verifyAccount = async (data) => {
    try {
      const { message, status } = await Service.post(
        ENDPOINTS.VERIFY_ADMIN,
        data,
      );
      return { status, message, data: null };
    } catch (error) {
      return errorResponse(error, "Verification failed");
    }
  };

  const resendOtp = async (data) => {
    try {
      const { message, status } = await Service.post(ENDPOINTS.RESEND_OTP, data);
      return { status, message };
    } catch {
      return { status: 500, message: "Failed to send code" };
    }
  };

  const superAdminCreateCompany = async (requestData) =>
    Service.post(ENDPOINTS.SIGNUP_URL, requestData);

  const updateBySuperAdmin = async (company_id, requestData) =>
    Service.put(
      `${ENDPOINTS.UPDATE_BY_SUPER_ADMIN}/${company_id}`,
      requestData,
    );

  const updateAdminPassword = async (requestData) =>
    Service.put(`${ENDPOINTS.UPDATE_PASSWORD}/${sysAdmin?.admin_id}`, requestData);

  const logOut = async () => {
    setSysAdmin(null);
    localStorage.removeItem("auth");
    localStorage.clear();
    window?.location?.reload();
  };

  const value = useMemo(
    () => ({
      sysAdmin,
      singleAdmin,
      companyName,
      selectedMenu,
      setSysAdmin,
      setSingleAdmin,
      setSelectedMenu,
      adminLogin,
      adminSignUp,
      verifyAccount,
      resendOtp,
      superAdminCreateCompany,
      updateBySuperAdmin,
      updateAdminPassword,
      logOut,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sysAdmin, singleAdmin, companyName, selectedMenu],
  );

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};
